import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import clsx from 'clsx';
import { Logo } from './ui';
import SmartLink from './SmartLink';

const LINKS = [
  { href: '/#features', label: 'Features' },
  { href: '/#audience', label: "Who It's For" },
  { href: '/#how-it-works', label: 'How It Works' },
  { href: '/about', label: 'About' },
];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-4 pt-4">
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={clsx(
          'mx-auto max-w-6xl rounded-2xl border px-4 py-3 transition-colors duration-300 sm:px-6',
          scrolled || isMenuOpen
            ? 'border-white/10 bg-ink-950/75 shadow-[0_10px_40px_-12px_rgba(0,0,0,0.8)] backdrop-blur-xl'
            : 'border-transparent bg-transparent'
        )}
      >
        <div className="flex items-center justify-between">
          <Logo />

          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
            {LINKS.map((link) => (
              <SmartLink
                key={link.href}
                to={link.href}
                className="rounded-full px-4 py-2 text-sm font-medium text-slate-400 transition-colors duration-200 hover:bg-white/[0.06] hover:text-white"
              >
                {link.label}
              </SmartLink>
            ))}
          </nav>

          <SmartLink to="/#get-started" className="btn-primary hidden !px-5 !py-2.5 !text-sm md:inline-flex">
            Get Started
          </SmartLink>

          <button
            type="button"
            className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-xl text-slate-300 transition-colors hover:bg-white/[0.06] hover:text-white md:hidden"
            onClick={() => setIsMenuOpen((open) => !open)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        <AnimatePresence initial={false}>
          {isMenuOpen && (
            <motion.nav
              key="mobile-nav"
              aria-label="Mobile"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden md:hidden"
            >
              <div className="flex flex-col gap-1 pb-2 pt-4">
                {LINKS.map((link) => (
                  <SmartLink
                    key={link.href}
                    to={link.href}
                    onClick={closeMenu}
                    className="rounded-xl px-3 py-3 text-base font-medium text-slate-300 transition-colors hover:bg-white/[0.06] hover:text-white"
                  >
                    {link.label}
                  </SmartLink>
                ))}
                <SmartLink to="/#get-started" onClick={closeMenu} className="btn-primary mt-3 w-full">
                  Get Started
                </SmartLink>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </motion.div>
    </header>
  );
};

export default Navbar;
